import { findKey, isEqual, isMatch } from 'lodash'
import { computed, IObservableArray, makeAutoObservable, observable } from 'mobx'
import { schema } from 'prose-core'
import { ALIGNMENT, ExtractStrongNodeSpecAttributes } from 'prose-core'
import { redoDepth, undoDepth } from 'prosemirror-history'
import { MarkType, Node, NodeType } from 'prosemirror-model'
import { NodeSelection, Selection, TextSelection } from 'prosemirror-state'
import { findParentNodeOfType } from 'prosemirror-utils'
import { type EditorView } from 'prosemirror-view'
import React, { createContext } from 'react'

import { TEXT_STYLES } from 'common/Prose/constants'
import { getFontAttrValue, getIntFontAttrValue } from 'common/Prose/hooks/useFontAttrInSelection'
import {
  FORMAT_OPTIONS,
  NumberedListFormat,
} from 'common/Prose/Toolbar/components/Format/components/ToggleListPane/NumberedListButton/util'
import { BorderSelection } from 'common/Prose/utils/table/BorderSelection'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'

export enum SelectionType {
  Empty,
  Node,
  Border,
  Cell,
  InMention,
  Unknown,
}

export interface SelectionInfo {}

export const AlmanacEditorStateContext = createContext<AlmanacEditorState | null>(null)
export class AlmanacEditorState {
  selection!: Selection
  view!: EditorView

  activeWeightsList: IObservableArray<any> = observable([])
  activeFamiliesList: IObservableArray<any> = observable([])
  activeFontSizeList: IObservableArray<number> = observable([])

  fontAttrs: IObservableArray<any> = observable([])
  highlightAttrs: IObservableArray<string> = observable([])
  colorAttrs: IObservableArray<string> = observable([])
  selectionType!: SelectionType
  selectedNodeType?: NodeType
  isSelectedNodeCommentable?: boolean

  activeMarks: Set<MarkType> = new Set()
  allNodeTypes: Set<NodeType> = new Set()
  activeNodes: Set<NodeType> = new Set()
  activeStyles: Set<keyof typeof TEXT_STYLES> = new Set()
  activeAlignments: Set<ALIGNMENT> = new Set()

  activeUlMarkerFormats: Set<string> = new Set()
  activeOlMarkerFormats: Set<string> = new Set()

  /**Selection is entirely contained within these types */
  containingNodeTypes: Set<NodeType> = new Set()

  undoDepth = 0
  redoDepth = 0
  columnCount = 1

  constructor(view: EditorView) {
    makeAutoObservable(this, {
      activeColors: computed.struct,
      highlightColors: computed.struct,
      activeWeights: computed.struct,
      activeFamilies: computed.struct,
      activeFontSizes: computed.struct,
    })

    this.updateFromView(view)
  }

  updateFromView(view: EditorView) {
    this.view = view
    this.selection = view.state.selection

    this.activeWeightsList.replace(Array.from(getFontAttrValue(view, schema.marks.font_weight)))
    this.activeFamiliesList.replace(Array.from(getFontAttrValue(view, schema.marks.font_family)))
    this.activeFontSizeList.replace(Array.from(getIntFontAttrValue(view, schema.marks.font_size)))

    this.colorAttrs.replace(Array.from(getFontAttrValue(view, schema.marks.colored)))
    this.highlightAttrs.replace(Array.from(getFontAttrValue(view, schema.marks.highlighted)))

    this.getSelectionType(view)
    this.getSelectedNodeCommentable()

    this.undoDepth = undoDepth(view.state)
    this.redoDepth = redoDepth(view.state)

    this.getColumnCount(view)
    this.getActiveMarks(view)
    this.getActiveNodeTypes(view)
    this.getActiveStyles(view)
    this.getActiveAlignments(view)

    this.getAllNodeTypes(view)

    this.getContainingNodesTypes(view)
    this.getListMarkerFormats(view)
    this.getOlMarkerFormats(view)
  }

  get fontWeight() {
    if (this.activeWeights.length === 1) return String(this.activeWeights[0])
    else return ''
  }

  get activeColors() {
    return this.colorAttrs
  }

  get highlightColors() {
    // Highlight will not be applied to next input, show default color
    if (this.selection.empty && !this.activeMarks.has(schema.marks.highlighted)) return ['#FFF']
    return this.highlightAttrs
  }

  get activeWeights() {
    return this.activeWeightsList.slice()
  }

  get activeFamilies() {
    return this.activeFamiliesList.slice()
  }

  get activeFontSizes() {
    return this.activeFontSizeList.slice()
  }

  selectionIncludesSomeNodeTypes(...nodeTypes: NodeType[]) {
    return nodeTypes.some((t) => this.activeNodes.has(t))
  }

  isSelectionNodeType(...nodeTypes: NodeType[]) {
    return this.selection instanceof NodeSelection && nodeTypes.includes(this.selection.node.type)
  }

  getSelectionType(view: EditorView) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    this.selectionType = this.selection.empty
      ? SelectionType.Empty
      : // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      this.selection instanceof NodeSelection
      ? SelectionType.Node
      : // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      this.selection instanceof BorderSelection
      ? SelectionType.Border
      : this.selection instanceof CellSelection
      ? SelectionType.Cell
      : SelectionType.Unknown

    const possibleMention = view.state.doc.nodeAt(Math.max(0, this.selection.from - 1))
    if (
      this.selection instanceof TextSelection &&
      this.selection.$cursor &&
      possibleMention &&
      possibleMention.type === schema.nodes.smartLink
    ) {
      this.selectionType = SelectionType.InMention
    }

    if (this.selection instanceof NodeSelection) this.selectedNodeType = this.selection.node.type
    else this.selectedNodeType = undefined
  }

  getSelectedNodeCommentable() {
    this.isSelectedNodeCommentable =
      this.selection instanceof NodeSelection && Array.isArray(this.selection.node.attrs.uuids)
  }

  getActiveMarks(view: EditorView) {
    // NOTE: this.activeMarks.empty() causes components to re-render causing flickers
    const activeMarks: Set<MarkType> = new Set()

    let { $from, empty, ranges } = view.state.selection

    if (empty) {
      if (!view.state.storedMarks) {
        // As per https://discuss.prosemirror.net/t/understanding-togglemark-and-storedmarks/2682
        // storedMarks == null means "don't do anything special for the next insertion" (which mean inherit the marks
        // around the cursor)
        // storedMarks === [] means "make sure the next insertion doesn't have any marks set".
        // storedMarks === [mark1, mark2] means "only apply mark1 and mark2 to the next insertion".
        $from.marks().forEach((m) => activeMarks.add(m.type))
      } else {
        view.state.storedMarks.forEach((m) => activeMarks.add(m.type))
      }
    } else {
      ranges.forEach((r) => {
        view.state.doc.nodesBetween(r.$from.pos, r.$to.pos, (n) => {
          n.marks.forEach((m) => {
            activeMarks.add(m.type)
          })
        })
      })
    }

    this.activeMarks = activeMarks
  }

  getColumnCount(view: EditorView) {
    const $colGroupPos = findParentNodeOfType(view.state.schema.nodes.column_group)(view.state.selection)
    this.columnCount = $colGroupPos ? $colGroupPos.node.content.childCount : 1
  }

  /**Find types for nodes that completely contain all selection ranges */
  getContainingNodesTypes(view: EditorView) {
    const rangeNodesList: Array<Set<Node>> = []

    for (const { $from, $to } of view.state.selection.ranges) {
      const foundNodes = new Set<Node>()

      const rangeNodes = new Set<Node>()
      rangeNodesList.push(rangeNodes)

      let depth = $from.depth
      let parent: Node | undefined
      do {
        parent = $from.node(depth)
        foundNodes.add(parent)
        depth--
      } while (depth > 0 && parent)

      depth = $to.depth
      parent = undefined
      do {
        parent = $to.node(depth)
        if (foundNodes.has(parent)) rangeNodes.add(parent)
        depth--
      } while (depth > 0 && parent)
    }

    const containingNodeTypes = new Set<NodeType>()
    for (const rangeNode of rangeNodesList[0])
      if (rangeNodesList.every((l) => l.has(rangeNode))) {
        containingNodeTypes.add(rangeNode.type)
        this.containingNodeTypes.add(rangeNode.type)
      }

    for (const nodeType of this.containingNodeTypes)
      if (!containingNodeTypes.has(nodeType)) this.containingNodeTypes.delete(nodeType)
  }

  getActiveNodeTypes(view: EditorView) {
    const foundNodeTypes = new Set<NodeType>()
    for (const { $from, $to } of view.state.selection.ranges) {
      view.state.doc.nodesBetween($from.pos, $to.pos, (n) => {
        this.activeNodes.add(n.type)
        foundNodeTypes.add(n.type)
      })
    }

    for (const nodeType of this.activeNodes) if (!foundNodeTypes.has(nodeType)) this.activeNodes.delete(nodeType)
  }

  // TODO: Iterate over the whole doc only once and do activeNodes while doing this
  getAllNodeTypes(view: EditorView) {
    const foundNodeTypes = new Set<NodeType>()
    view.state.doc.nodesBetween(0, view.state.doc.nodeSize - 2, (n) => {
      this.allNodeTypes.add(n.type)
      foundNodeTypes.add(n.type)
    })

    for (const nodeType of this.allNodeTypes) if (!foundNodeTypes.has(nodeType)) this.allNodeTypes.delete(nodeType)
  }

  getActiveAlignments(view: EditorView) {
    const foundAlignments = new Set<ALIGNMENT>()
    for (const { $from, $to } of view.state.selection.ranges) {
      view.state.doc.nodesBetween($from.pos, $to.pos, (n) => {
        if (n.attrs.alignment) {
          this.activeAlignments.add(n.attrs.alignment)
          foundAlignments.add(n.attrs.alignment)
        } else if (n.attrs.float) {
          this.activeAlignments.add(n.attrs.float)
          foundAlignments.add(n.attrs.float)
        } else if (n.attrs.alignment === null && n.attrs.float === undefined) {
          this.activeAlignments.add(ALIGNMENT.Left)
          foundAlignments.add(ALIGNMENT.Left)
        }
      })
    }

    for (const alignment of this.activeAlignments)
      if (!foundAlignments.has(alignment)) this.activeAlignments.delete(alignment)
  }

  getListMarkerFormats(view: EditorView) {
    const foundUlMarkerFormats = new Set<string>()

    for (const { $from, $to } of view.state.selection.ranges) {
      view.state.doc.nodesBetween($from.pos, $to.pos, (n) => {
        if (n.type === schema.nodes.unordered_list) foundUlMarkerFormats.add(n.attrs.markerFormat)
        this.activeUlMarkerFormats.add(n.attrs.markerFormat)
      })
    }

    for (const markerFormat of this.activeUlMarkerFormats)
      if (!foundUlMarkerFormats.has(markerFormat)) this.activeUlMarkerFormats.delete(markerFormat)
  }

  getOlMarkerFormats(view: EditorView) {
    const selectedOlAttrs: ExtractStrongNodeSpecAttributes<typeof schema.nodes.ordered_list>[] = []
    const foundOlMarkerFormats = new Set<keyof typeof FORMAT_OPTIONS>()

    for (const { $from, $to } of view.state.selection.ranges) {
      view.state.doc.nodesBetween($from.pos, $to.pos, (n) => {
        if (n.type === schema.nodes.ordered_list) {
          const markerAttrs = {
            counterSeparator: n.attrs.counterSeparator,
            counterStyles: n.attrs.counterStyles,
            flattenMarker: n.attrs.flattenMarker,
          } as (typeof selectedOlAttrs)[0]

          const matchingFormatKey =
            findKey(FORMAT_OPTIONS, (opt) => isEqual(markerAttrs, opt.attrs)) ||
            findKey(FORMAT_OPTIONS, (opt) => isMatch(markerAttrs, opt.attrs))

          if (matchingFormatKey && matchingFormatKey in FORMAT_OPTIONS) {
            this.activeOlMarkerFormats.add(matchingFormatKey)
            foundOlMarkerFormats.add(matchingFormatKey as NumberedListFormat)
          }
        }
      })
    }

    for (const markerFormat of this.activeOlMarkerFormats)
      if (!foundOlMarkerFormats.has(markerFormat as NumberedListFormat)) this.activeOlMarkerFormats.delete(markerFormat)
  }

  getActiveStyles(view: EditorView) {
    const foundStyles = new Set<keyof typeof TEXT_STYLES>()

    for (const { $from, $to } of view.state.selection.ranges) {
      view.state.doc.nodesBetween($from.pos, $to.pos, (n) => {
        const matchingTextStyleKey = findKey(
          TEXT_STYLES,
          (style) => style.type === n.type && isMatch(n.attrs, style.attrs),
        )

        if (matchingTextStyleKey) {
          this.activeStyles.add(matchingTextStyleKey)
          foundStyles.add(matchingTextStyleKey)
        }
      })
    }

    for (const style of this.activeStyles) if (!foundStyles.has(style)) this.activeStyles.delete(style)
  }
}

export const useAlmanacEditorState = () => {
  const context = React.useContext(AlmanacEditorStateContext)
  return context
}
